import { useCallback, useEffect, useState } from "react";
import {
  SanitizedProjectRecordCenterClientViewV1Schema,
  CLIENT_PROJECT_RECORD_RETENTION_SUMMARY_V1,
  type SanitizedProjectRecordCenterClientViewV1,
} from "@enrein/shared-contracts";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { V10ClientLinkViewProps } from "../types.js";

function timelineStatusLabel(status: SanitizedProjectRecordCenterClientViewV1["timeline"][number]["status"]): string {
  switch (status) {
    case "complete":
      return "Complete";
    case "pending":
      return "In progress";
    case "blocked":
      return "Needs attention";
    case "skipped":
      return "Skipped";
    default:
      return "In progress";
  }
}

export function V10RecordTab({
  active,
  api,
  linkId,
  token,
  isExternalProof,
}: {
  readonly active: boolean;
  readonly api: V10ClientLinkViewProps["api"];
  readonly linkId: string;
  readonly token: string;
  readonly isExternalProof: boolean;
}) {
  const [loading, setLoading] = useState(isExternalProof);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<SanitizedProjectRecordCenterClientViewV1 | null>(null);

  const load = useCallback(async () => {
    if (!active || !isExternalProof) return;
    setLoading(true);
    setError(null);
    try {
      const next = SanitizedProjectRecordCenterClientViewV1Schema.parse(
        await api.getProjectRecordCenter(linkId, token),
      );
      setView(next);
    } catch (err) {
      setView(null);
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    } finally {
      setLoading(false);
    }
  }, [active, api, isExternalProof, linkId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      className={`v10-cl-tab-panel${active ? " active" : ""}`}
      role="tabpanel"
      id="v10-tab-record"
      aria-labelledby="v10-tab-btn-record"
      hidden={!active}
    >
      {!isExternalProof ? (
        <article className="v10-cl-card">
          <h3>Record retention</h3>
          <p>{CLIENT_PROJECT_RECORD_RETENTION_SUMMARY_V1.headline}</p>
          <p className="v10-cl-muted">{CLIENT_PROJECT_RECORD_RETENTION_SUMMARY_V1.body}</p>
          <p className="v10-cl-muted">{CLIENT_PROJECT_RECORD_RETENTION_SUMMARY_V1.postDeparturePolicy}</p>
          <p className="v10-cl-muted">
            Full delivery record details are available for external proof projects when that workflow is active.
          </p>
        </article>
      ) : loading && !view ? (
        <p className="v10-cl-muted">Loading delivery record…</p>
      ) : error ? (
        <article className="v10-cl-card">
          <h3>Record unavailable</h3>
          <p className="v10-cl-muted">{error}</p>
          <button type="button" className="v10-cl-btn" onClick={() => void load()}>
            Try again
          </button>
        </article>
      ) : !view ? (
        <article className="v10-cl-card">
          <h3>Record not ready yet</h3>
          <p className="v10-cl-muted">Enrein prepared the record and proof when your project steps complete.</p>
        </article>
      ) : (
        <div className="v10-cl-grid-two">
          <article className="v10-cl-card">
            <h3>Record</h3>
            <p>
              <strong>{view.summary.projectModeLabel}</strong>
            </p>
            <p>{view.summary.currentStatusLabel}</p>
            {view.summary.nextAction ? (
              <p className="v10-cl-muted">
                <strong>Next:</strong> {view.summary.nextAction}
              </p>
            ) : null}
          </article>
          <article className="v10-cl-card">
            <h3>Proof documents</h3>
            {view.documents.length === 0 ? (
              <p className="v10-cl-muted">Proof documents will appear as each step completes.</p>
            ) : (
              <ul className="v10-cl-record-docs">
                {view.documents.map((doc) => (
                  <li key={`${doc.docType}-${doc.label}`}>
                    <strong>{doc.label}</strong> — {doc.lifecycleStatusLabel}
                    {doc.available ? "" : " (not ready)"}
                  </li>
                ))}
              </ul>
            )}
          </article>
          {view.timeline.length > 0 ? (
            <article className="v10-cl-card v10-cl-record-timeline">
              <h3>Timeline</h3>
              <ul>
                {view.timeline.map((entry) => (
                  <li key={entry.entryId}>
                    <p>
                      <strong>{entry.headline}</strong> — {timelineStatusLabel(entry.status)}
                    </p>
                    <p className="v10-cl-muted">{entry.summary}</p>
                  </li>
                ))}
              </ul>
            </article>
          ) : null}
          <article className="v10-cl-card">
            <h3>Retention policy</h3>
            <p>{CLIENT_PROJECT_RECORD_RETENTION_SUMMARY_V1.headline}</p>
            <p className="v10-cl-muted">{CLIENT_PROJECT_RECORD_RETENTION_SUMMARY_V1.postDeparturePolicy}</p>
          </article>
          {view.handoff ? (
            <article className="v10-cl-card">
              <h3>Handoff</h3>
              <p>{view.handoff.clientSummary}</p>
              <p className="v10-cl-muted">
                {view.handoff.handoffAvailable ? "Handoff package available." : "Handoff not ready yet."}
              </p>
            </article>
          ) : null}
          {view.warnings && view.warnings.length > 0 ? (
            <article className="v10-cl-card">
              <h3>Notes</h3>
              <ul>
                {view.warnings.map((warning) => (
                  <li key={warning.code}>{warning.message}</li>
                ))}
              </ul>
            </article>
          ) : null}
        </div>
      )}
    </section>
  );
}
